import React from 'react';
import 'react-toggle/style.css';
import styled from 'styled-components';
import { DASHBOARD_REFRESH_INTERVAL_MS } from 'src/config';
import IRhapsodyDashboard from 'src/ServerEntities/IRhapsodyDashboard';
import IRhapsodyServer from 'src/ServerEntities/IRhapsodyServer';
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import dateConverter from 'src/UsefulFunctions/dateConverter';
import { SessionContext } from 'src/Views/SessionContext';
import { getRhapsodyDashboard, getRhapsodyHeader } from '../ramService';
import useCookie from 'src/CustomHooks/useCookie';
import AlertsPanel from '../Components/AlertsPanel';
import ComponentPanel from '../Components/ComponentPanel';
import MessageThroughputPanel from '../Components/MessageThroughputPanel';
import QueuesPanel from '../Components/QueuesPanel';
import ServerDetailsPanel from '../Components/ServeDetailsPanel';
import { RolesContext } from 'src/Roles/RolesContext';
import { ContentContainer } from '../Intersystems/IntersystemsDashboard';
import ServerSelector from 'src/SharedComponents/Select/ServerSelector';

const SiteContainer = styled.ul`
    margin-top: 24px;
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: flex-start;
`;

interface IProps {
    selectedTypes: string[],
    changeRole: boolean,
    setChangeRole: (changeRole: boolean) => void
};

const TieInfo = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 30px;
`;

const TieTitle = styled.div`
    font-size: 25px;
    font-weight: bold;
`;

const VersionTitle = styled.div`
    font-size: 20px;
    font-weight: bold;
    color: #1577AE;
`;

const LicenceTitle = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 18px;
    font-weight: bold;
`;

const LicenceItem = styled.div`
`;

const LeftColumn = styled.div`
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    flex-direction: row;
    height: 100%;
`;

const RightColumn = styled.div`
    display: flex;
    gap: 15px;
    flex-direction: column;
    align-items: stretch;
    flex: 1 1 auto;
`;

const HorizontalContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    & > div,
    & > main {
        flex: 1 0 auto;
        margin: 0 12px;
    }
    & > div:nth-child(1),
    & > main:nth-child(1) {
        flex: 1 0 auto;
        margin-left: 0;
    }
    & > div:nth-last-child(1),
    & > main:nth-last-child(1) {
        flex: 1 0 auto;
        margin-right: 0;
    }
`;

export const getExpiryDate = (server: IRhapsodyServer) => {
    if (!server) {
        return "";
    }
    return server.licenceExpiryDate ? (dateConverter(server.licenceExpiryDate) === 'N/A' 
          ? 'Does not expire' 
          : dateConverter(server.licenceExpiryDate))
      : 'Does not expire';
};

const RhapsodyDashboard = (props: IProps) => {
    const { state, dispatch } = React.useContext(SessionContext);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [data, setData] = React.useState(undefined as unknown as IRhapsodyServer[]);
    const [selectedNamespace, setSelectedNamespace] = useCookie<string>('selectedNamespace', '');
    const selectedServer = state.selectedSever ?? 0;

    const setSelectedServer = (index: number) => {
        dispatch({ type: "setSelectedServer", payload: index });
    };
    const [dashboard, setDashboard] = React.useState(undefined as unknown as IRhapsodyDashboard);    
    const roleContext = React.useContext(RolesContext);
    const headerInfo = state.headerInfo ? state.headerInfo : data;
    const currentRole = roleContext.state.role;

    React.useEffect(() => {
        const fetchHeaderAndDashboard = () => {
            setLoading(true);
            getRhapsodyHeader(
                state.webToken,
                (serverData: IRhapsodyServer[]) => {
                    setData(serverData);
                    setError("");
                    dispatch({ type: "setHeaderInfo", payload: serverData });

                    if (serverData[selectedServer]) {
                        getRhapsodyDashboard(
                            serverData[selectedServer].id,
                            state.webToken,
                            (dashboardData: IRhapsodyDashboard) => {
                                setDashboard(dashboardData);
                                setError("");
                                setLoading(false);
                            },
                            (errorMessage: string) => {
                                setError(errorMessage);
                                setLoading(false);
                            }
                        );
                    } else {
                        setLoading(false);
                    }
                },
                (errorMessage: string) => {
                    setError(errorMessage);
                    setLoading(false);
                },
                selectedNamespace && selectedNamespace.trim() !== '' ? selectedNamespace : undefined
            );
        };

        fetchHeaderAndDashboard();
        const interval = setInterval(fetchHeaderAndDashboard, DASHBOARD_REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [state.webToken, currentRole, selectedServer, selectedNamespace, dispatch]);

    return <ContentContainer>
        <LoadingIndicator type="Linear" show={loading} />
        {error && <ErrorBox>{error}</ErrorBox>}
        {headerInfo && headerInfo[selectedServer] && <TieInfo>
            <TieTitle>Rhapsody Integration Engine</TieTitle>
            <VersionTitle>Version {headerInfo[selectedServer].licenceVersion}</VersionTitle>
            <LicenceTitle>
                <LicenceItem>Licence: {headerInfo[selectedServer].licenceType ?? headerInfo[selectedServer].licenceEndUser}</LicenceItem>
                <LicenceItem>Expiry: {headerInfo ? getExpiryDate(headerInfo[selectedServer]) : ""}</LicenceItem>
            </LicenceTitle>
        </TieInfo>}
        <ServerSelector headerInfo={headerInfo} selectedNamespace={selectedNamespace}
            selectedServer={selectedServer} setSelectedNamespace={setSelectedNamespace} setSelectedServer={setSelectedServer}/>
        <SiteContainer>
            <LeftColumn>
                {dashboard && <ServerDetailsPanel token={state.webToken} data={dashboard.server} interystems={false} />}
            </LeftColumn>
            <RightColumn>
                <HorizontalContainer>
                    {dashboard && <MessageThroughputPanel today={dashboard.todayMessageCounts} yesterday={dashboard.yesterdayMessageCounts} />}
                </HorizontalContainer>
                <HorizontalContainer>
                    {dashboard && <ComponentPanel data={dashboard.components} />}
                    {dashboard && <AlertsPanel data={dashboard.alerts} forwardTo="/Ram/Monitor Alerts" />}
                    {dashboard && <QueuesPanel data={dashboard.queues} />}
                </HorizontalContainer>
            </RightColumn>
        </SiteContainer>
    </ContentContainer>;
};

export default RhapsodyDashboard;
